import { format, subDays, subYears } from 'date-fns';
import {
  saveSettings,
  getStoredSettings,
} from '../../../../../utilities/storage';
import { CONVERSATIONS_STORAGE_KEY } from '../../../../conversations/useConversations';
import { ISO_FORMAT } from '../../../../../components/Datepicker';
import {
  AnalyticsMetricValueLabel,
  AnalyticsMetricValue,
} from '../../../../conversations/const';
import { AnalyticsMetric } from '../../../const';

export type AnalyticsPeriod = 'TODAY' | 'WEEK' | 'MONTH' | 'YEAR';

export interface AnalyticsRedirectParams {
  metricName: string;
  metricValue: string;
  agentId: string;
  period: AnalyticsPeriod;
}

const getDateRangeFromPeriod = (period: AnalyticsPeriod) => {
  const now = new Date();
  let from: string, to: string;

  switch (period) {
    case 'TODAY':
      from = format(subDays(now, 1), ISO_FORMAT);
      to = format(now, ISO_FORMAT);
      break;
    case 'WEEK':
      from = format(subDays(now, 7), ISO_FORMAT);
      to = format(now, ISO_FORMAT);
      break;
    case 'MONTH':
      from = format(subDays(now, 30), ISO_FORMAT);
      to = format(now, ISO_FORMAT);
      break;
    case 'YEAR':
      from = format(subYears(now, 1), ISO_FORMAT);
      to = format(now, ISO_FORMAT);
      break;
    default:
      from = format(subDays(now, 1), ISO_FORMAT);
      to = format(now, ISO_FORMAT);
  }

  return { from, to };
};

export const redirectToConversationsWithFilters = (
  params: AnalyticsRedirectParams
) => {
  const { metricName, metricValue, agentId, period } = params;
  const { from, to } = getDateRangeFromPeriod(period);

  const existingSettings = getStoredSettings(CONVERSATIONS_STORAGE_KEY);
  const existingFilters = existingSettings?.filters || {};
  const existingSort = existingSettings?.sort || null;

  const analyticsFilters = {
    ...existingFilters,
    agent_id: agentId,
    started_at: from,
    ended_at: to,
    metric_name: metricName,
    metric_value: metricValue,
  };

  saveSettings(CONVERSATIONS_STORAGE_KEY, analyticsFilters, existingSort);

  window.location.href = '/conversation';
};

export interface KnowledgeBaseRedirectParams {
  knowledgeBaseId: string;
}

export const redirectToConversationsWithKnowledgeBase = (
  params: KnowledgeBaseRedirectParams
) => {
  const { knowledgeBaseId } = params;

  const existingSettings = getStoredSettings(CONVERSATIONS_STORAGE_KEY);
  const existingFilters = existingSettings?.filters || {};
  const existingSort = existingSettings?.sort || null;

  const knowledgeBaseFilters = {
    ...existingFilters,
    knowledge_base_id: knowledgeBaseId,
  };

  saveSettings(CONVERSATIONS_STORAGE_KEY, knowledgeBaseFilters, existingSort);

  window.location.href = '/conversation';
};

export const handlePieChartClick = (
  chartType: string,
  segmentName: string,
  agentId: string,
  period: AnalyticsPeriod
) => {
  let metricName: string;
  let metricValue: string;

  switch (chartType) {
    case AnalyticsMetric.SUCCESS_RATE:
      metricName = AnalyticsMetric.SUCCESS_RATE;
      metricValue =
        segmentName === AnalyticsMetricValueLabel.SUCCESSFUL_CONVERSATIONS
          ? AnalyticsMetricValue.SUCCESSFUL_CONVERSATIONS
          : AnalyticsMetricValue.NOT_SUCCESSFUL_CONVERSATIONS;
      break;
    case AnalyticsMetric.ERROR_RATE:
      metricName = AnalyticsMetric.ERROR_RATE;
      metricValue =
        segmentName === AnalyticsMetricValueLabel.CONVERSATIONS_WITH_ERROR
          ? AnalyticsMetricValue.CONVERSATIONS_WITH_ERROR
          : AnalyticsMetricValue.CONVERSATIONS_WITHOUT_ERROR;
      break;
    case AnalyticsMetric.USER_ABANDONED:
      metricName = AnalyticsMetric.USER_ABANDONED;
      metricValue =
        segmentName === AnalyticsMetricValueLabel.CONVERSATIONS_LEFT
          ? AnalyticsMetricValue.CONVERSATIONS_LEFT
          : AnalyticsMetricValue.CONVERSATIONS_COMPLETED;
      break;
    case AnalyticsMetric.TOPIC_TAGS:
      metricName = AnalyticsMetric.TOPIC_TAGS;
      metricValue = segmentName;
      break;
    case AnalyticsMetric.MOOD_TAGS:
      metricName = AnalyticsMetric.MOOD_TAGS;
      metricValue = segmentName;
      break;
    default:
      return;
  }

  redirectToConversationsWithFilters({
    metricName,
    metricValue,
    agentId,
    period,
  });
};
